import React, { useCallback, useEffect, useState } from "react";
import {
  Drawer,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Divider,
} from "antd";
import dayjs from "dayjs";
import { RecipePreview } from "./RecipePreview";
import {
  useCreateProduction,
  useProductLookup,
  useRecipe,
} from "./useProduction";
import {
  DRAWER_FORM_WIDTH,
  drawerFooter,
  drawerTitle,
} from "../../../components";

const { Option } = Select;
const { TextArea } = Input;

interface Props {
  open: boolean;
  onClose: () => void;
}

export const NewProductionDrawer: React.FC<Props> = ({ open, onClose }) => {
  const [form] = Form.useForm();
  const [selectedProductId, setSelectedProductId] = useState<string | null>(
    null,
  );

  const { products, setProductSearch } = useProductLookup();
  const { recipe, recipeLoading } = useRecipe(selectedProductId);

  const productionQty = Form.useWatch("quantity", form) || 0;
  const additionalCost = Form.useWatch("additional_cost", form) || 0;

  const handleClose = useCallback(() => {
    form.resetFields();
    setSelectedProductId(null);
    onClose();
  }, [form, onClose]);

  const mutation = useCreateProduction(handleClose);

  const handleProductChange = (id: string) => {
    setSelectedProductId(id);
    form.setFieldsValue({ product_stock_id: undefined });
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      mutation.mutate({
        ...values,
        product_id: selectedProductId,
        production_date:
          values.production_date?.format("YYYY-MM-DD") ||
          dayjs().format("YYYY-MM-DD"),
      });
    } catch {}
  };

  useEffect(() => {
    if (open) {
      form.setFieldsValue({ production_date: dayjs() });
    }
  }, [open, form]);

  const selectedProduct = products.find((p: any) => p.id === selectedProductId);

  return (
    <Drawer
      title={drawerTitle("New Production Run")}
      open={open}
      onClose={handleClose}
      width={DRAWER_FORM_WIDTH}
      styles={{ body: { padding: "20px 24px" } }}
      footer={drawerFooter({
        actions: [
          { label: "Cancel", onClick: handleClose },
          {
            label: "Record Production",
            type: "primary",
            loading: mutation.isPending,
            disabled:
              recipeLoading ||
              (!!selectedProductId &&
                (!recipe || !recipe.items || recipe.items.length === 0)),
            onClick: handleSubmit,
          },
        ],
      })}
    >
      <Form form={form} layout="vertical" size="middle" requiredMark>
        {/* Product Search Selection */}
        <Form.Item
          name="product_id"
          label="Target Product"
          rules={[
            { required: true, message: "Please select a target product." },
          ]}
        >
          <Select
            showSearch
            placeholder="Search product by name or SKU…"
            filterOption={false}
            onSearch={setProductSearch}
            onChange={handleProductChange}
            notFoundContent={null}
          >
            {products.map((p: any) => (
              <Option key={p.id} value={p.id}>
                <span className="font-semibold text-gray-700">{p.title}</span>
                <span className="ml-2 text-xs text-gray-400 font-mono">
                  ({p.sku_code})
                </span>
              </Option>
            ))}
          </Select>
        </Form.Item>

        {/* Stock Batch selection */}
        {selectedProductId && (
          <Form.Item
            name="product_stock_id"
            label="Product Stock Batch"
            tooltip="Select an existing batch or leave blank to automatically create a new batch."
          >
            <Select placeholder="Auto-create a new batch" allowClear>
              <Option value="new">Create new batch</Option>
              {selectedProduct?.stocks?.map((s: any) => (
                <Option key={s.id} value={s.id}>
                  {s.batch_no} — {s.quantity} units{" "}
                  {s.is_default ? " (default)" : ""}
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}

        {/* Quantity & Date Row */}
        <div className="grid grid-cols-2 gap-4">
          <Form.Item
            name="quantity"
            label="Quantity to Produce"
            rules={[
              { required: true, message: "Required quantity." },
              {
                type: "number",
                min: 1,
                message: "Must produce at least 1 unit.",
              },
            ]}
          >
            <InputNumber
              placeholder="0"
              min={1}
              style={{ width: "100%" }}
              addonAfter="units"
            />
          </Form.Item>

          <Form.Item
            name="production_date"
            label="Production Date"
            rules={[{ required: true, message: "Please specify date." }]}
          >
            <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
          </Form.Item>
        </div>

        {/* Additional overhead cost */}
        <Form.Item
          name="additional_cost"
          label="Overhead / Additional Cost"
          tooltip="Electricity, labor, water, etc. allocated to this production batch."
        >
          <InputNumber
            placeholder="0.00"
            min={0}
            step={0.01}
            precision={2}
            style={{ width: "100%" }}
            addonBefore="LKR"
          />
        </Form.Item>

        {/* Optional notes */}
        <Form.Item name="note" label="Production Run Note">
          <TextArea
            rows={3}
            placeholder="Add any details or notes regarding this production batch…"
            maxLength={500}
            showCount
          />
        </Form.Item>

        {/* Scaled recipe details */}
        {selectedProductId && (
          <>
            <Divider className="my-4" />
            <div className="mb-2 text-[11px] font-bold uppercase tracking-wider text-gray-400">
              Ingredients & Cost Estimation
            </div>
            <RecipePreview
              recipe={recipe}
              recipeLoading={recipeLoading}
              productionQty={productionQty}
              additionalCost={additionalCost}
            />
          </>
        )}
      </Form>
    </Drawer>
  );
};
